import Registry from 'core/ComponentRegistry';
import Component from 'core/Component';
import { deepMerge } from 'toolbox/deepMerge';
import comparisonStore from 'stores/ComparisonStore';
import { on, off, trigger } from 'toolbox/event';
import { mix } from 'core/mixwith';
import Accessibility from 'mixins/Accessibility';

/**
 * This is a description of the ComparisonButton constructor function.
 * @class
 * @classdesc ComparisonButton class designed to handle client-side logic
 * related to comparison button section functionality.
 * @extends Component
 */
export default class ComparisonButton extends mix(Component).with(Accessibility) {
    /**
     * Constructor of the class that mainly merge the options of the components
     * @param {HTMLElement} element HTMLElement of the component
     * @param {object} options options that belongs to the component
     */
    constructor(element, options = {}) {
        super(element, deepMerge({
            pid: null, // current product's id
            masterId: null, // current master product's id
            isForbidden: false, // if true, will be disabled
            text: {
                compare: 'compare',
                remove: 'remove',
            },
            classNames: {
                active: null,
            },
            a11y: {
                compareAriaMessage: '', // aria live message to notify user that product has been added to selection
                removeAriaMessage: '', // aria live message to notify user that product has been removed from selection
            },
        }, options), comparisonStore);
    }

    /**
     * All selectors must be cached. Never cache elements that are out of the component scope
     */
    initCache() {
        this.selectors.buttonText = this.element.querySelector('[data-js-button-text]');
        this.selectors.statusMessage = document.querySelector('[data-js-global-status-message]');
        this.selectors.productTile = this.element.closest('[data-component="product/ProductTile"]');

        this.classes = {
            hidden: 'h-hidden',
            disabled: 'm-disabled',
        };
    }

    /**
     * Init the different state of the component
     * It helps to avoid heavy DOM manipulation
     */
    initState() {
        this.state.isHidden = true;
        this.state.isDisabled = false;
        this.state.isInComparison = false;

        let { instanceId } = this.options;
        if (!instanceId) {
            const comparisonIdElement = this.element.closest('[data-comparison-id]');
            if (comparisonIdElement) {
                instanceId = comparisonIdElement.getAttribute('data-comparison-id');
            }
        }

        this.state.instanceId = instanceId || 'default';
    }

    /**
     * Should contain only event listeners and nothing else
     * All the event handlers should be into a separated function. No usage of anonymous function
     */
    bindEvents() {
        on('click', this.element, this.onClick.bind(this));
    }

    /**
     * After init
     * Run any script after the component is fully initialized
     */
    afterInit() {
        if (this.options.isForbidden) {
            this.element.classList.add(this.classes.disabled);
        }

        this.updateButton();
        Registry.afterRegistered(this.selectors.productTile).then(() => {
            this.onProductTileInitialized();
        });
    }

    /**
     * Store update hook
     * @param {Object} state new state
     */
    storeUpdated(state) {
        this.setButtonAction(state);
    }

    /**
     * Set button action [Compare/Remove] based on product presence in already added products lists
     * @param {Object} state store state
     * @param {Object} initial is initial toggle
     */
    setButtonAction(state, initial) {
        const currentState = state[this.state.instanceId];
        if (!currentState) {
            return;
        }
        this.toggleComparisonButton(currentState, initial || false);
        const pid = this.options.masterId || this.options.pid;
        const isInComparison = currentState.products.some(item => pid === item.masterId || pid === item.pid);
        const message = this.state.isInComparison ? this.options.a11y.removeAriaMessage : this.options.a11y.compareAriaMessage;

        if (isInComparison !== this.state.isInComparison) {
            this.state.isInComparison = isInComparison;
            this.updateButton();
            this.updateAria(this.selectors.statusMessage, message);
        }

        this.toggleDisabling(currentState.limitExceeded);

        trigger('comparisonButton.selected', this.element, {
            bubbles: true,
            isInComparison: currentState.showComparisonButtons && this.state.isInComparison,
        });
    }

    /**
     * Toggle disabling of the comparison button
     * All the event handlers should be into a separated function. No usage of anonymous function
     * @param {Boolean} limitExceeded is limit exceeded
     */
    toggleDisabling(limitExceeded) {
        const isDisabled = (limitExceeded && !this.state.isInComparison) || this.options.isForbidden;
        if (this.state.isDisabled === isDisabled) {
            return;
        }
        this.state.isDisabled = isDisabled;
        if (isDisabled) {
            this.element.classList.add(this.classes.disabled);
        } else {
            this.element.classList.remove(this.classes.disabled);
        }
    }

    /**
     * Toggle comparison button and action button
     * All the event handlers should be into a separated function. No usage of anonymous function
     * @param {Object} state store state
     * @param {Object} initial is initial toggle
     */
    toggleComparisonButton(state, initial = false) {
        if ((!this.state.isHidden !== state.showComparisonButtons) || (initial && state.showComparisonButtons)) {
            this.state.isHidden = !state.showComparisonButtons;
            if (this.state.isHidden) {
                this.element.classList.add(this.classes.hidden);
            } else {
                this.element.classList.remove(this.classes.hidden);
            }
            trigger('comparisonButton.cta.update', this.element, {
                bubbles: true,
                show: !this.state.isHidden,
            });
        }
    }

    /**
     * Should contain only event listeners and nothing else
     * All the event handlers should be into a separated function. No usage of anonymous function
     */
    updateButton() {
        if (this.state.isInComparison) {
            this.selectors.buttonText.innerHTML = this.options.text.remove;
            if (this.options.classNames.active) {
                this.element.classList.remove(this.options.classNames.active);
            }
        } else {
            this.selectors.buttonText.innerHTML = this.options.text.compare;

            if (this.options.classNames.active) {
                this.element.classList.add(this.options.classNames.active);
            }
        }
    }

    /**
     * Toggle comparison button after producttile's initialization
     * @listens 'producttile.initialized'
     */
    onProductTileInitialized() {
        const state = comparisonStore.get();
        this.setButtonAction(state, true);
    }

    /**
     * Adds product to the comparison
     * @listens 'click'
     */
    onClick() {
        const event = this.state.isInComparison ? 'removeProduct' : 'addProduct';

        comparisonStore.dispatch(event, {
            pid: this.options.pid,
            masterId: this.options.masterId,
            instanceId: this.state.instanceId,
        });

        trigger('comparisonButton.selected', this.element, {
            bubbles: true,
            isInComparison: this.state.isInComparison,
        });
    }

    /**
     * Destroy is called automatically after the component is being removed from the DOM
     * You must always destroy the listeners attached to an element to avoid any memory leaks
     */
    destroy() {
        off(this.element);
    }
}
